import * as React from 'react'
import { type UseQueryResult } from '@tanstack/react-query'
import { type EventSeat } from '@/components/events/event-seat-answers'
import {
  ListPager,
  STAFF_LIST_PAGE_SIZE,
  useCursorPages,
  type CursorPager,
  type PagedResponse,
} from '@/components/templates/list-pager'
import { Button } from '@/components/ui/button'
import { Table } from '@/components/ui/table'
import { OrderRows, OrdersHeader, OrdersToolbar } from './event-orders-table'
import type { OwnedRecord } from '@/components/OwnedChip'

export type EventOrder = OwnedRecord & {
  id: number
  status: 'confirmed' | 'cancelled'
  quantity: number
  ordered_date: string
  ordered_time: string
  time_zone_label: string
  currency: string
  buyer: { name: string; email: string }
  answers: { prompt: string; value: string }[]
  seats: EventSeat[]
  checked_in_attendees?: string[]
  invoice: null | { id?: number; status: string; balance: string; total: string }
  money_state?: { status: string; open_balance_cents: number }
}

export type BuyerGroup = {
  buyer: { id: number; name: string; email: string }
  orders: EventOrder[]
}
type EventOrderBook = PagedResponse & { buyer_groups: BuyerGroup[]; orders: EventOrder[] }

/** The Orders tab: the event's order book, newest first, each Order expandable to its seats. */
export function EventOrders({
  eventId,
  readOnly,
  onNew,
}: {
  eventId: number
  readOnly: boolean
  onNew?: () => void
}) {
  const [expandedOrderId, setExpandedOrderId] = React.useState<number>()
  const [search, setSearch] = React.useState('')
  const [invoiceStatus, setInvoiceStatus] = React.useState('all')
  const [owner, setOwner] = React.useState<string | undefined>()
  const [sort, setSort] = React.useState<'placed' | 'total'>('placed')
  const [direction, setDirection] = React.useState<'asc' | 'desc'>('desc')
  const { query: ordersQuery, pager } = useCursorPages<EventOrderBook>({
    queryKey: ['event-orders', eventId],
    path: eventOrdersPath(eventId, { search, invoiceStatus, owner, sort, direction }),
    limit: STAFF_LIST_PAGE_SIZE,
  })

  return (
    <OrdersTable
      eventId={eventId}
      query={ordersQuery}
      pager={pager}
      readOnly={readOnly}
      action={
        onNew && (
          <Button type="button" variant="outline" onClick={onNew}>
            New order
          </Button>
        )
      }
      expandedOrderId={expandedOrderId}
      onExpandedOrderChange={setExpandedOrderId}
      search={search}
      invoiceStatus={invoiceStatus}
      onSearch={setSearch}
      onInvoiceStatus={setInvoiceStatus}
      owner={owner}
      onOwner={setOwner}
      sort={sort}
      direction={direction}
      onSort={(nextSort) => {
        if (nextSort === sort) setDirection((current) => (current === 'asc' ? 'desc' : 'asc'))
        else {
          setSort(nextSort)
          setDirection('asc')
        }
      }}
    />
  )
}

function OrdersTable({
  eventId,
  query,
  pager,
  readOnly,
  action,
  expandedOrderId,
  onExpandedOrderChange,
  search,
  invoiceStatus,
  onSearch,
  onInvoiceStatus,
  owner,
  onOwner,
  sort,
  direction,
  onSort,
}: {
  eventId: number
  query: UseQueryResult<EventOrderBook>
  pager: CursorPager | null
  readOnly: boolean
  action?: React.ReactNode
  expandedOrderId?: number
  onExpandedOrderChange: (id: number | undefined) => void
  search: string
  invoiceStatus: string
  onSearch: (value: string) => void
  onInvoiceStatus: (value: string) => void
  owner?: string
  onOwner: (value?: string) => void
  sort: 'placed' | 'total'
  direction: 'asc' | 'desc'
  onSort: (sort: 'placed' | 'total') => void
}) {
  if (query.isLoading) return <TableMessage>Loading orders…</TableMessage>
  if (query.isError) return <TableMessage>Orders could not be loaded.</TableMessage>

  const groups = query.data?.buyer_groups ?? []

  return (
    <>
      <OrdersToolbar
        search={search}
        invoiceStatus={invoiceStatus}
        onSearch={onSearch}
        onInvoiceStatus={onInvoiceStatus}
        owner={owner}
        onOwner={onOwner}
        action={action}
      />
      <div className="overflow-hidden rounded-md border border-border bg-card">
        <ListPager pager={pager} label="Orders" position="top" />
        <Table>
          <OrdersHeader sort={sort} direction={direction} onSort={onSort} />
          <OrderRows
            groups={groups}
            eventId={eventId}
            expandedOrderId={expandedOrderId}
            onExpandedOrderChange={onExpandedOrderChange}
            readOnly={readOnly}
          />
        </Table>
        {groups.length === 0 && (
          <p className="px-4 py-12 text-center text-caption text-muted-foreground">
            No orders yet.
          </p>
        )}
        <ListPager pager={pager} label="Orders" position="bottom" />
      </div>
    </>
  )
}

function TableMessage({ children }: { children: React.ReactNode }) {
  return (
    <div className="rounded-md border border-dashed border-border px-4 py-12 text-center text-caption text-muted-foreground">
      {children}
    </div>
  )
}

function eventOrdersPath(
  eventId: number,
  filters: {
    search: string
    invoiceStatus: string
    owner?: string
    sort: string
    direction: string
  },
) {
  const params = new URLSearchParams()
  if (filters.search) params.set('order_search', filters.search)
  if (filters.invoiceStatus !== 'all') params.set('order_invoice_status', filters.invoiceStatus)
  if (filters.owner) params.set('owner', filters.owner)
  params.set('order_sort', filters.sort)
  params.set('order_direction', filters.direction)
  const query = params.toString()
  return query ? `/api/v1/events/${eventId}/orders?${query}` : `/api/v1/events/${eventId}/orders`
}
